import { useEffect, FC, useState } from 'react';
import useGooglePayChargeTransaction from '@/hooks/useApi/useGooglePayChargeTransaction';
import usePayPal from '@/hooks/useApi/usePayPal';
import useVenmo from '@/hooks/useApi/useVenmo';
import DigitalWalletTransactionInfo from './DigitalWalletTransactionInfo';
import { useDataFetchIndicator } from '@/hooks';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import {
  GooglePayChargeTransactionResponse,
  PayPalWithdrawTransactionResponse,
  VenmoWithdrawTransactionResponse,
  TransactionId,
} from 'vopay';

type DigitalWalletTransaction =
  | GooglePayChargeTransactionResponse
  | PayPalWithdrawTransactionResponse
  | VenmoWithdrawTransactionResponse;

const DigitalWalletTransactionContainer: FC<
  TransactionId & { transactionType: 'GooglePay' | 'PayPal' | 'Venmo'; fullName: string; jwt: string }
> = ({ TransactionID, transactionType, fullName, jwt }) => {
  const [currentTransactionId, setCurrentTransactionId] = useState<string>();
  const [transaction, setTransaction] = useState<DigitalWalletTransaction>();
  const [, setLoading] = useDataFetchIndicator();
  const alertDialog = useAlertDialog();

  // Hook usage based on transaction type
  const { fetchGooglePayChargeTransaction } = useGooglePayChargeTransaction({ bearer: jwt });
  const { fetchPayPalWithdrawTransaction } = usePayPal({ bearer: jwt });
  const { fetchVenmoWithdrawTransaction } = useVenmo({ bearer: jwt });

  useEffect(() => {
    const fetchTransaction = async () => {
      try {
        setLoading(true);
        if (transactionType === 'GooglePay') {
          const resp = await fetchGooglePayChargeTransaction(TransactionID);
          setTransaction(resp);
        } else if (transactionType === 'PayPal') {
          const resp = await fetchPayPalWithdrawTransaction(TransactionID);
          setTransaction(resp);
        } else if (transactionType === 'Venmo') {
          const resp = await fetchVenmoWithdrawTransaction(TransactionID);
          setTransaction(resp);
        }
      } catch (error) {
        alertDialog.error('Error', error instanceof Error ? error.message : 'An unexpected error occurred.');
      } finally {
        setLoading(false);
      }
    };
    fetchTransaction();
  }, [currentTransactionId, transactionType]);

  if (currentTransactionId !== TransactionID) {
    setCurrentTransactionId(TransactionID);
  }

  return (
    <div>
      {transaction && (
        <DigitalWalletTransactionInfo
          transaction={transaction}
          transactionType={transactionType}
          fullName={fullName}
          jwt={jwt}
        />
      )}
    </div>
  );
};

export default DigitalWalletTransactionContainer;
